import cloneDeep from 'lodash.clonedeep';
import { select, put, call, CallEffect, PutEffect, SelectEffect } from 'redux-saga/effects';

import { EditorRedoAction, editorRedoSuccess } from 'js/actionCreators/editorActions';
import { updateBitmapFonts } from 'js/actionCreators/fontActions';
import { setActiveElements, setSelectedAudioClip, updateScribe } from 'js/actionCreators/scribeActions';

import { areSameActiveElements } from '../sagaHelpers/areSameActiveElements';
import { clearCanvasSelectionReadyForUpdates } from '../sagaHelpers/clearCanvasSelectionReadyForUpdates';
import { isTextElementsChanged } from '../sagaHelpers/isTextElementsChanged';
import { getUndoObjects } from '../selectors';

export function* redo(
  action: EditorRedoAction
): Generator<SelectEffect | PutEffect | CallEffect, void, ReturnType<typeof getUndoObjects>> {
  const { scribeId } = action;
  const { activeScribe, activeScribePast, activeScribeFuture } = yield select(getUndoObjects, scribeId);

  // Do nothing if there is nothing in the redo future
  if (!activeScribeFuture.length || !activeScribe) return;

  yield call(clearCanvasSelectionReadyForUpdates);

  const newActiveScribe = activeScribeFuture[0];
  const newFuture = activeScribeFuture.slice(1);
  const newPast = [...activeScribePast, cloneDeep(activeScribe)];

  const nextSelectedAudioClipId = newActiveScribe.nextSelectedAudioClipId;
  const newSelectedAudioClip = newActiveScribe.audioClips?.find(audioClip => audioClip.id === nextSelectedAudioClipId);

  yield put(editorRedoSuccess(newPast, newFuture));
  yield put(updateScribe(newActiveScribe, true));

  const areActiveElementsChanged = areSameActiveElements(
    (activeScribe.selectedElementIds, newActiveScribe.nextSelectedElementIds)
  );
  if (areActiveElementsChanged) {
    yield put(setActiveElements(newActiveScribe.nextSelectedElementIds));
  }
  yield put(setSelectedAudioClip(newSelectedAudioClip));

  const newTextElements = newActiveScribe.elements.filter(e => e.type === 'Text');
  const prevTextElements = activeScribe.elements.filter(e => e.type === 'Text');
  const isNeedUpdateBitmapFonts = isTextElementsChanged(newTextElements, prevTextElements);
  if (isNeedUpdateBitmapFonts) yield put(updateBitmapFonts(newTextElements));
}
